import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { platform, type PlatformAuditRecord } from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';

import { origemSemSessao, rotuloDaAcao } from './labels';

/**
 * Trilha da plataforma.
 *
 * Ela e separada da trilha de cada empresa e sobrevive a exclusao da empresa alvo.
 * O banco recusa editar ou apagar qualquer linha.
 *
 * As consultas tambem ficam aqui: quem administra enxerga todas as empresas, e
 * enxergar sem registro e o que a plataforma recusa. Por padrao a tela mostra so o
 * que alterou alguma coisa, para que as mudancas nao se percam entre as leituras.
 */

const limite = 25;

export function PlatformTrailPage() {
  const [comConsultas, setComConsultas] = useState(false);
  const [offset, setOffset] = useState(0);
  const [aberta, setAberta] = useState<PlatformAuditRecord | null>(null);

  const consulta = useQuery({
    queryKey: ['platform', 'audit', { comConsultas, offset }],
    queryFn: ({ signal }) =>
      platform.audit({ only_changes: !comConsultas, limit: limite, offset }, signal),
  });

  const colunas: Column<PlatformAuditRecord>[] = [
    {
      key: 'quando',
      header: 'Data e hora',
      render: (linha) => (
        <span className="tabular-nums">{formatDateTime(linha.occurred_at)}</span>
      ),
    },
    {
      key: 'quem',
      header: 'Quem',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{linha.actor_label}</p>
          {linha.actor_user_id === null ? (
            <Badge tone="neutral">{origemSemSessao(linha.actor_label)}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'acao',
      header: 'Acao',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{rotuloDaAcao(linha.action)}</p>
          <p className="truncate font-mono text-2xs text-ink-500">{linha.action}</p>
        </div>
      ),
    },
    {
      key: 'empresa',
      header: 'Empresa',
      hideBelow: 'md',
      render: (linha) =>
        linha.target_tenant_id ? (
          <Link
            to={`/admin/empresas/${linha.target_tenant_id}`}
            onClick={(evento) => evento.stopPropagation()}
            className="font-mono text-xs text-brand-600 hover:underline"
          >
            {linha.target_label || linha.target_tenant_id}
          </Link>
        ) : (
          <span className="text-ink-400">plataforma</span>
        ),
    },
    {
      key: 'detalhe',
      header: 'Detalhe',
      align: 'right',
      render: (linha) => (
        <AcaoComIcone
          acao="visualizar"
          rotulo="Ver"
          aria-label={`Ver antes e depois de ${rotuloDaAcao(linha.action)}`}
          onClick={() => setAberta(linha)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Trilha da plataforma"
        description="Quem administrou o que, e quando. Nenhuma linha se edita nem se apaga."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-brand-500)]"
              checked={comConsultas}
              onChange={(evento) => {
                setComConsultas(evento.target.checked);
                setOffset(0);
              }}
            />
            Mostrar tambem as consultas
          </label>
          <span className="ml-auto text-xs text-ink-600">
            {consulta.data?.total ?? 0} registros
          </span>
        </div>

        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar a trilha">
            {consulta.error.message}
          </Alert>
        ) : null}

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(linha) => linha.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="lock"
              title="Trilha ainda vazia"
              description="Cada acao da administracao da plataforma passa a aparecer aqui."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {aberta ? <AntesEDepois linha={aberta} onClose={() => setAberta(null)} /> : null}
    </div>
  );
}

function AntesEDepois({
  linha,
  onClose,
}: {
  linha: PlatformAuditRecord;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={rotuloDaAcao(linha.action)}
      description={`${formatDateTime(linha.occurred_at)}, ${linha.actor_label}`}
      footer={<Button onClick={onClose}>Fechar</Button>}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Estado titulo="Antes" valor={linha.before} />
        <Estado titulo="Depois" valor={linha.after} />
      </div>
      {linha.request_id ? (
        <p className="mt-4 text-2xs text-ink-500">Requisicao {linha.request_id}</p>
      ) : null}
    </Modal>
  );
}

function Estado({ titulo, valor }: { titulo: string; valor: unknown }) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-ink-700">{titulo}</h3>
      {valor === null || valor === undefined ? (
        <p className="mt-1 text-sm text-ink-400">Nada registrado.</p>
      ) : (
        <pre className="mt-1 max-h-80 overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-2xs text-ink-800">
          {JSON.stringify(valor, null, 2)}
        </pre>
      )}
    </section>
  );
}
